/** Predefined ophthalmic exam finding options for quick documentation. */

import { mergeStringListsUnique, normalizeCatalogToken } from './clinicalCatalog';

export const CUSTOM_OPHTHALMIC_FINDINGS_STORAGE_KEY = 'emr_custom_ophthalmic_findings_v1';
export const OPHTHALMIC_FINDINGS_UPDATED_EVENT = 'emr-ophthalmic-findings-updated';

export const EYE_SIDES = ['OD', 'OS', 'OU'];

/** Exam form fields stored as { right, left } per eye. */
export const EXAM_FINDING_FIELD_KEYS = [
    'lids',
    'adnexa',
    'anterior_segment',
    'pupils',
    'ocular_motility',
    'posterior_segment',
    'neuro_ophthalmic',
];

const defaultFindingPair = () => ({ right: '', left: '' });

const EYE_PREFIX_RE = /^(OD|OS|OU|Right|Left)\s*:\s*(.*)$/i;

/** Normalize legacy string or per-eye object to { right, left }. */
export const normalizeFindingPair = (value) => {
    if (value && typeof value === 'object') {
        if ('right' in value || 'left' in value || 'od' in value || 'os' in value) {
            return {
                right: String(value.right ?? value.od ?? value.OD ?? '').trim(),
                left: String(value.left ?? value.os ?? value.OS ?? '').trim(),
            };
        }
    }
    if (typeof value === 'string') return splitLegacyFindingText(value);
    return defaultFindingPair();
};

/** Parse OD:/OS:/OU: prefixed lines; otherwise duplicate text to both eyes. */
export const splitLegacyFindingText = (text) => {
    const base = defaultFindingPair();
    const raw = String(text || '').trim();
    if (!raw) return base;

    const buckets = { right: [], left: [], both: [] };
    let hasEyePrefix = false;

    for (const line of raw.split(/\n+/)) {
        const trimmed = line.trim();
        if (!trimmed) continue;
        const match = trimmed.match(EYE_PREFIX_RE);
        if (match) {
            hasEyePrefix = true;
            const token = match[1].toLowerCase();
            const content = match[2].trim();
            if (!content) continue;
            if (token === 'od' || token === 'right') buckets.right.push(content);
            else if (token === 'os' || token === 'left') buckets.left.push(content);
            else buckets.both.push(content);
        } else if (hasEyePrefix) {
            const target =
                buckets.right.length >= buckets.left.length ? buckets.right : buckets.left;
            if (target.length) target[target.length - 1] += ` ${trimmed}`;
        }
    }

    if (hasEyePrefix) {
        const join = (arr) => arr.join('\n');
        const shared = join(buckets.both);
        base.right = [join(buckets.right), shared].filter(Boolean).join('\n');
        base.left = [join(buckets.left), shared].filter(Boolean).join('\n');
        return base;
    }

    return { right: raw, left: raw };
};

export const hasFindingPairContent = (value) => {
    const pair = normalizeFindingPair(value);
    return Boolean(pair.right.trim() || pair.left.trim());
};

/** Format per-eye findings for reports, PDF, and portal (lang: en | ar). */
export const formatFindingPairForDisplay = (value, lang = 'en') => {
    const pair = normalizeFindingPair(value);
    if (!pair.right.trim() && !pair.left.trim()) return '';
    if (pair.right.trim() && pair.right.trim() === pair.left.trim()) {
        return pair.right.trim();
    }
    const odLabel = lang === 'ar' ? 'يمين (OD)' : 'OD';
    const osLabel = lang === 'ar' ? 'يسار (OS)' : 'OS';
    const lines = [];
    if (pair.right.trim()) lines.push(`${odLabel}: ${pair.right.trim()}`);
    if (pair.left.trim()) lines.push(`${osLabel}: ${pair.left.trim()}`);
    return lines.join('\n');
};

export const normalizeExamFindingFields = (data) => {
    if (!data || typeof data !== 'object') return {};
    const out = {};
    for (const key of EXAM_FINDING_FIELD_KEYS) {
        out[key] = normalizeFindingPair(data[key]);
    }
    return out;
};

export const buildDefaultExamFindings = () => {
    const out = {};
    for (const key of EXAM_FINDING_FIELD_KEYS) {
        out[key] = defaultFindingPair();
    }
    return out;
};

const readCustomFindingsStore = () => {
    try {
        const raw = localStorage.getItem(CUSTOM_OPHTHALMIC_FINDINGS_STORAGE_KEY);
        if (!raw) return { en: {}, ar: {} };
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return { en: {}, ar: {} };
        return {
            en: parsed.en && typeof parsed.en === 'object' ? parsed.en : {},
            ar: parsed.ar && typeof parsed.ar === 'object' ? parsed.ar : {},
        };
    } catch {
        return { en: {}, ar: {} };
    }
};

const writeCustomFindingsStore = (store) => {
    try {
        localStorage.setItem(CUSTOM_OPHTHALMIC_FINDINGS_STORAGE_KEY, JSON.stringify(store));
        window.dispatchEvent(new CustomEvent(OPHTHALMIC_FINDINGS_UPDATED_EVENT));
    } catch {
        console.warn('Unable to persist custom ophthalmic findings');
    }
};

/** Bundled options only (no clinician-added rows). */
export const getBundledFindingCategoryOptions = (categoryKey, lang = 'en') => {
    const category = OPHTHALMIC_FINDING_CATEGORIES[categoryKey];
    if (!category) return [];
    if (lang === 'ar' && Array.isArray(category.optionsAr) && category.optionsAr.length) {
        return category.optionsAr;
    }
    return category.options || [];
};

/** @returns {string[]} Custom findings for one category and language. */
export const getCustomFindingsForCategory = (categoryKey, lang = 'en') => {
    const bucket = lang === 'ar' ? readCustomFindingsStore().ar : readCustomFindingsStore().en;
    const list = bucket[categoryKey];
    return Array.isArray(list) ? list : [];
};

export const getFindingCategoryOptions = (categoryKey, lang = 'en') => {
    const bundled = getBundledFindingCategoryOptions(categoryKey, lang);
    const custom = getCustomFindingsForCategory(categoryKey, lang);
    return mergeStringListsUnique(bundled, custom);
};

export const normalizeFindingToken = (value) => normalizeCatalogToken(value);

export const findingMatchesCatalog = (text, categoryKey, lang = 'en') => {
    const token = normalizeFindingToken(text);
    if (!token) return true;
    return getFindingCategoryOptions(categoryKey, lang).some(
        (option) => normalizeFindingToken(option) === token
    );
};

/** Last non-empty line in free text, with OD:/OS:/OU: prefix stripped. */
export const extractLastFindingLine = (text) => {
    const lines = String(text || '')
        .split(/\n+/)
        .map((line) => line.trim())
        .filter(Boolean);
    if (!lines.length) return '';
    const last = lines[lines.length - 1];
    const match = last.match(EYE_PREFIX_RE);
    return (match ? match[2] : last).trim();
};

/**
 * Persist a clinician-typed finding for future dropdowns (deduped, case-insensitive).
 * @returns {string} Canonical stored label
 */
export const addCustomOphthalmicFinding = (categoryKey, text, lang = 'en') => {
    if (!OPHTHALMIC_FINDING_CATEGORIES[categoryKey]) {
        throw new Error('Unknown finding category');
    }
    const label = String(text || '').trim().replace(/\s+/g, ' ');
    if (!label) throw new Error('Finding text is required');
    if (findingMatchesCatalog(label, categoryKey, lang)) {
        return label;
    }

    const langKey = lang === 'ar' ? 'ar' : 'en';
    const store = readCustomFindingsStore();
    const bucket = { ...store[langKey] };
    const merged = mergeStringListsUnique(bucket[categoryKey] || [], [label]);
    bucket[categoryKey] = merged;
    writeCustomFindingsStore({ ...store, [langKey]: bucket });
    return label;
};

export const OPHTHALMIC_FINDING_CATEGORIES = {
    lids: {
        label: 'Lids & Lashes',
        labelAr: 'الجفون والرموش',
        placeholder: 'Document lid position, margin, lashes, and periocular skin…',
        options: [
            'Normal lids and lashes',
            'Blepharitis — anterior',
            'Blepharitis — posterior (MGD)',
            'Chalazion',
            'Hordeolum (stye)',
            'Entropion',
            'Ectropion',
            'Ptosis — mild',
            'Ptosis — moderate/severe',
            'Trichiasis',
            'Madarosis',
            'Lid edema',
            'Lid retraction',
            'Lagophthalmos',
            'Dermatochalasis',
            'Xanthelasma',
            'Lid lesion — benign appearance',
            'Lid lesion — suspicious',
            'Contact dermatitis',
            'Preseptal cellulitis',
            'Orbital cellulitis suspected',
            'Meibomian gland dysfunction (MGD)',
            'Collarettes at lash base',
            'Demodex blepharitis suspected',
            'Floppy eyelid syndrome',
            'Eyelid margin telangiectasia',
            'Symblepharon',
            'Ankyloblepharon',
            'Canthal dystopia',
        ],
    },
    adnexa: {
        label: 'Adnexa & Orbit',
        labelAr: 'الملحقات والمحجر',
        placeholder: 'Lacrimal system, orbit, and periorbital tissues…',
        options: [
            'Normal adnexa',
            'Dacryocystitis',
            'Mucocele / dacryocele',
            'Epiphora — lacrimal obstruction suspected',
            'Punctal stenosis',
            'Canaliculitis',
            'Enophthalmos',
            'Proptosis — mild',
            'Proptosis — significant',
            'Orbital mass suspected',
            'Preseptal edema',
            'Periorbital erythema',
            'Traumatic ecchymosis',
            'Dacryoadenitis',
            'Lacrimal gland mass suspected',
            'Nasolacrimal duct obstruction',
            'Punctal ectropion',
            'Canalicular trauma suspected',
            'Thyroid eye disease — adnexal signs',
            'Lid lag on downgaze',
        ],
    },
    anterior_segment: {
        label: 'Anterior Segment',
        labelAr: 'المقدمة',
        placeholder: 'Cornea, conjunctiva, anterior chamber, iris, lens…',
        options: [
            'Quiet anterior segment',
            'Normal cornea and conjunctiva',
            'Conjunctival injection — mild',
            'Conjunctival injection — moderate/severe',
            'Chemosis',
            'Subconjunctival hemorrhage',
            'Pterygium — nasal',
            'Pterygium — encroaching visual axis',
            'Pinguecula',
            'Cornea clear',
            'Corneal abrasion',
            'Corneal ulcer suspected',
            'Keratitis — bacterial suspected',
            'Keratitis — viral (dendritic) suspected',
            'Filamentary keratitis',
            'Corneal edema',
            'Corneal opacity / scar',
            'Keratoconus — suspected',
            'Band keratopathy',
            'KPs — fine',
            'KPs — granulomatous',
            'Anterior chamber deep and quiet',
            'Shallow anterior chamber',
            'Cells and flare — grade 1+',
            'Cells and flare — grade 2+',
            'Cells and flare — grade 3+',
            'Hyphema',
            'Hypopyon',
            'Iris normal',
            'Iris atrophy',
            'Posterior synechiae',
            'Peripheral iridotomy patent',
            'Rubeosis iridis',
            'Lens clear',
            'Cataract — nuclear sclerosis',
            'Cataract — cortical',
            'Cataract — posterior subcapsular',
            'Pseudophakia — well centered',
            'Aphakia',
            'PCIOL — subluxation suspected',
            'Dry eye — tear break-up time reduced',
            'Superior limbic keratoconjunctivitis (SLK)',
            'Giant papillary conjunctivitis (GPC)',
            'Vernal shield ulcer',
            'Limbal vernal infiltrates',
            'Phlyctenule',
            'Interstitial keratitis',
            'Exposure keratopathy',
            'Recurrent corneal erosion',
            'Fuchs endothelial dystrophy',
            'Iris nevus',
            'Iris melanoma suspected',
            'Zonular dehiscence suspected',
            'Vitreous in anterior chamber',
        ],
    },
    pupils: {
        label: 'Pupils',
        labelAr: 'البؤبؤ',
        placeholder: 'Size, shape, reactivity, RAPD, and pharmacologic dilation…',
        options: [
            'PERRLA',
            'Pupils equal and round',
            'Reactive to light',
            'Sluggish reaction to light',
            'Non-reactive pupil',
            'Relative afferent pupillary defect (RAPD)',
            'Anisocoria — physiologic',
            'Anisocoria — pathologic',
            'Mid-dilated pupil — fixed',
            'Irregular pupil',
            'Posterior synechiae affecting pupil',
            'Pharmacologically dilated',
            'Pharmacologically constricted (miotic)',
            'Adie tonic pupil suspected',
            'Horner syndrome suspected',
            'Light-near dissociation',
            'Spasm of the near reflex',
            'Corectopia',
            'Polycoria',
            'Sector iris atrophy',
        ],
    },
    ocular_motility: {
        label: 'Ocular Motility & Alignment',
        labelAr: 'حركة العين والمحاذاة',
        placeholder: 'Extraocular movements, alignment, and diplopia…',
        options: [
            'Full extraocular movements',
            'Orthophoria',
            'Esotropia',
            'Exotropia',
            'Hypertropia / hypotropia',
            'Restrictive motility suspected',
            'CN III palsy suspected',
            'CN IV palsy suspected',
            'CN VI palsy suspected',
            'Internuclear ophthalmoplegia suspected',
            'Diplopia — horizontal',
            'Diplopia — vertical',
            'Diplopia — on distance gaze',
            'Diplopia — on near gaze',
            'Nystagmus — horizontal',
            'Nystagmus — vertical',
            'Thyroid eye disease pattern suspected',
            'Brown syndrome suspected',
            'Duane retraction syndrome',
            'Orbital blowout fracture pattern',
            'Abnormal head posture for fusion',
            'Cover test — orthophoria at distance',
            'Cover test — esophoria at near',
        ],
    },
    posterior_segment: {
        label: 'Posterior Segment',
        labelAr: 'القسم الخلفي',
        placeholder: 'Vitreous, optic disc, macula, vessels, and periphery…',
        options: [
            'Clear media',
            'Vitreous clear',
            'Posterior vitreous detachment',
            'Vitreous hemorrhage',
            'Vitreous opacities / floaters symptomatic',
            'Optic disc normal color and contour',
            'Cup-to-disc ratio ~0.3',
            'Cup-to-disc ratio ~0.5',
            'Cup-to-disc ratio ≥0.7 — glaucoma suspect',
            'Disc pallor',
            'Disc edema',
            'Parapapillary atrophy',
            'Drusen — hard',
            'Drusen — soft / confluent',
            'Macula flat',
            'Macular edema suspected',
            'Macular hole suspected',
            'Epiretinal membrane',
            'Geographic atrophy',
            'Chorioretinal scar',
            'CSR suspected',
            'CNV / wet AMD suspected',
            'Diabetic retinopathy — NPDR mild',
            'Diabetic retinopathy — NPDR moderate',
            'Diabetic retinopathy — severe / PDR',
            'Dot-blot hemorrhages',
            'Hard exudates',
            'Cotton-wool spots',
            'Retinal veins tortuous',
            'Arteriovenous nicking',
            'Branch retinal vein occlusion suspected',
            'Central retinal vein occlusion suspected',
            'Branch retinal artery occlusion suspected',
            'Rhegmatogenous retinal detachment suspected',
            'Lattice degeneration',
            'Choroidal nevus',
            'Choroidal mass — refer urgently',
            'Vitreomacular traction suspected',
            'Macular pucker / ERM with distortion',
            'Retinoschisis',
            'Retinal tear — horseshoe',
            'Retinal tear — operculated',
            'Choroidal rupture',
            'Commotio retinae',
            'Solar retinopathy suspected',
            'Hypertensive retinopathy — grade 2',
            'Hypertensive retinopathy — grade 3–4',
            'Retinal artery embolus visible',
            'Optic disc hemorrhage',
            'Peripapillary CNV suspected',
            'Myopic degeneration',
            'Peripheral drusen',
        ],
    },
    neuro_ophthalmic: {
        label: 'Neuro-ophthalmic',
        labelAr: 'عصبي بصري',
        placeholder: 'Visual fields, color vision, and related findings…',
        options: [
            'Confrontation visual fields full',
            'Visual field defect suspected',
            'Color vision grossly normal',
            'Dyschromatopsia',
            'Proptosis on comparison',
            'Facial sensation intact V1–V3',
            'Relative afferent defect on swinging flashlight',
            'Papilledema suspected',
            'Optic neuritis suspected',
            'Retrobulbar pain on eye movement',
            'Abnormal OKN',
            'Cranial nerve palsy pattern on exam',
        ],
        optionsAr: [
            'حقول المواجهة كاملة',
            'اشتباه عيب حقل بصري',
            'رؤية الألوان طبيعية إجمالاً',
            'عسر تمييز الألوان',
            'بروز العين عند المقارنة',
            'إحساس الوجه سليم V1–V3',
        ],
    },
};

export const appendFindingText = (current, finding, eye = null) => {
    if (!finding) return current || '';
    const prefix = eye && eye !== 'OU' ? `${eye}: ` : eye === 'OU' ? 'OU: ' : '';
    const line = `${prefix}${finding}`.trim();
    const base = (current || '').trim();
    if (!base) return line;
    if (base.split('\n').some((row) => row.trim() === line)) return base;
    return `${base}\n${line}`;
};
